/**
 * Guide engine v3 overlay - one ring (#1025, epic #1020). Spec R15.
 *
 * A ring traces the highlight element's bounding rect in the highlight's own
 * `color`; the UI derives a glow from that color. It is position:fixed, tracks
 * the element on scroll/resize, never mutates the app DOM, and is
 * pointer-events:none so the element stays fully clickable underneath (the reveal
 * is cosmetic only - a click advance fires whether or not the ring has lit). No
 * spotlight layer: the page stays visible behind it (a gated step's dim comes
 * from runtime/gating.js, not the ring).
 *
 * Pulse breathes the glow to draw the eye; reduced motion = steady glow, no pulse
 * (R15). Reveal is gated by `visible` (the Wheel-of-Fortune stagger lives in
 * GuideRings): hidden = opacity 0, shown = fade to 1.
 */

import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useElementRect } from './useElementRect';
import { useResolvedReducedMotion } from './motion';

// Ring standoff from the target rect (#1052, two rounds of owner feedback).
// Compact targets (buttons, table rows) keep a tight 8px so a ringed row never
// bleeds into its neighbors. Tall controls (a standard MUI outlined field is
// 56px) get 14px: their shrunk floating label overflows ~10px above the
// element rect, so anything tighter draws the ring line through the label.
const PAD_COMPACT = 8;
const PAD_TALL = 14;
const TALL_MIN_HEIGHT = 56;
const padFor = (rect) => (rect.height >= TALL_MIN_HEIGHT ? PAD_TALL : PAD_COMPACT);

const GuideRing = ({ highlight, visible = true, reducedMotion, zIndex }) => {
  const reduced = useResolvedReducedMotion(reducedMotion);
  // Always measure (even before this ring is revealed) so the Wheel-of-Fortune
  // reveal is a fade of an already-positioned ring, not a late mount.
  const rect = useElementRect(highlight.element);
  if (!rect) return null;

  const PAD = padFor(rect);
  const { color } = highlight;
  const steadyGlow = `0 0 0 3px ${alpha(color, 0.34)}, 0 0 12px 3px ${alpha(color, 0.5)}`;

  return (
    <Box
      aria-hidden
      data-testid="guide-ring"
      data-ring-color={color}
      data-ring-visible={visible ? 'true' : 'false'}
      data-zindex={zIndex}
      sx={(theme) => ({
        position: 'fixed',
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        border: `2px solid ${color}`,
        // container radius tracks the larger standoff so corners wrap the
        // target instead of clipping it (#1052)
        borderRadius: theme.ds.radius.container,
        boxShadow: steadyGlow,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: reduced ? 'none' : `opacity ${theme.ds.motion.base} ${theme.ds.motion.easeOut}`,
        zIndex,
        ...(reduced ? {} : {
          animation: 'guideRingPulse 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite',
          '@keyframes guideRingPulse': {
            '0%, 100%': { boxShadow: `0 0 0 3px ${alpha(color, 0.28)}, 0 0 10px 2px ${alpha(color, 0.4)}` },
            '50%': { boxShadow: `0 0 0 5px ${alpha(color, 0.45)}, 0 0 18px 5px ${alpha(color, 0.66)}` },
          },
        }),
      })}
    />
  );
};

export default GuideRing;
